import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { toast } from 'sonner';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';
import PrescriptionViewer from './PrescriptionViewer';
import './SearchPatient.css';

function SearchPatient({ doctorInfo, onSelectPatient, onIssueCertificate, initialSearch = '' }) {
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [patients, setPatients] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientHistory, setPatientHistory] = useState([]);
  const [familyList, setFamilyList] = useState([]);

  // Edit Profile States
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [viewingRx, setViewingRx] = useState(null);

  // revamping states
  const [sortBy, setSortBy] = useState('recent');
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [goToPageInput, setGoToPageInput] = useState('');

  const fetchPatients = async (query = '', currentPage = 1) => {
    setLoading(true);
    try {
      let dbQuery = supabase
        .from('patients')
        .select('*', { count: 'exact' })
        .eq('clinic_id', doctorInfo.clinic_id);

      if (query.trim()) {
        const cleanPhone = query.replace(/\D/g, '');
        const isPhone = cleanPhone.length >= 10;
        if (isPhone) {
          dbQuery = dbQuery.eq('phone', cleanPhone.slice(0, 10));
        } else {
          dbQuery = dbQuery.ilike('name', `%${query}%`);
        }
      }

      // Dynamic sorting
      if (sortBy === 'name') {
        dbQuery = dbQuery.order('name', { ascending: true });
      } else if (sortBy === 'age') {
        dbQuery = dbQuery.order('age', { ascending: false });
      } else {
        // Default: sort by most recent visit (last_visit desc, nulls last)
        dbQuery = dbQuery
          .order('last_visit', { ascending: false, nullsFirst: false })
          .order('created_at', { ascending: false });
      }

      // Range for 10 per page
      const from = (currentPage - 1) * 10;
      const to = currentPage * 10 - 1;
      dbQuery = dbQuery.range(from, to);

      const { data, count, error } = await dbQuery;
      if (error) throw error;

      setPatients(data || []);
      setTotalCount(count || 0);
    } catch (err) {
      toast.error('Error fetching patients: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (doctorInfo?.clinic_id) {
        fetchPatients(searchTerm, page);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, page, sortBy, doctorInfo?.clinic_id]);

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    setPage(1);
    fetchPatients(searchTerm, 1);
  };

  const totalPages = Math.ceil(totalCount / 10);

  const handlePrevPage = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleNextPage = () => {
    if (page < totalPages) setPage(page + 1);
  };

  async function handleViewDetails(patient) {
    setSelectedPatient(patient);
    setEditForm({ ...patient }); // Pre-fill edit form
    setIsEditing(false); // Ensure viewing mode by default
    setLoading(true);
    try {
      // 1. Fetch prescriptions history
      const { data, error } = await supabase
        .from('prescriptions')
        .select('*')
        .eq('patient_id', patient.id)
        .order('created_at', { ascending: false })
        .limit(10);
      if (error) throw error;
      setPatientHistory(data || []);

      // 2. Fetch connected family members
      const familyGroupId = patient.primary_contact_id || patient.id;
      const { data: family, error: familyErr } = await supabase
        .from('patients')
        .select('*')
        .or(`id.eq.${familyGroupId},primary_contact_id.eq.${familyGroupId}`)
        .eq('clinic_id', doctorInfo.clinic_id);

      if (!familyErr && family) {
        // filter out the currently selected patient
        setFamilyList(family.filter(f => f.id !== patient.id));
      } else {
        setFamilyList([]);
      }
    } catch (error) {
      console.error('Error fetching details:', error);
      setPatientHistory([]);
      setFamilyList([]);
    }
    setLoading(false);
  }

  function handleStartPrescription(patient) {
    if (onSelectPatient) onSelectPatient(patient);
  }

  async function handleUpdatePatient(e) {
    e.preventDefault();
    if (!editForm.phone || editForm.phone.trim().length !== 10) {
      toast.error('Please enter a valid 10-digit phone number.');
      return;
    }

    setLoading(true);
    try {
      // 1. Check if another patient already has this phone number
      const { data: existingPatient, error: existingError } = await supabase
        .from('patients')
        .select('id')
        .eq('phone', editForm.phone.trim())
        .neq('id', selectedPatient.id)
        .maybeSingle();

      if (existingError) throw existingError;
      if (existingPatient) {
        toast.error('This phone number is already registered to another patient.');
        setLoading(false);
        return;
      }

      // 2. Perform the update
      const { data, error } = await supabase
        .from('patients')
        .update({
          name: editForm.name,
          age: editForm.age,
          gender: editForm.gender,
          phone: editForm.phone
        })
        .eq('id', selectedPatient.id)
        .select()
        .single();

      if (error) throw error;

      setSelectedPatient(data); // Sync local state
      setIsEditing(false); // Close edit view
      toast.success('Patient profile updated successfully');

      // Update background search results silently
      setPatients(prev => prev.map(p => p.id === data.id ? data : p));
    } catch (error) {
      toast.error('Failed to update patient: ' + error.message);
    }
    setLoading(false);
  }

  const getPatientStatus = (patient) => {
    if (patient.conditions && patient.conditions.toLowerCase() !== 'none' && patient.conditions.toLowerCase() !== 'n/a') {
      return 'Active';
    }
    return 'Completed';
  };

  const handleGoToPage = (e) => {
    e.preventDefault();
    const targetPage = parseInt(goToPageInput);
    if (targetPage >= 1 && targetPage <= totalPages) {
      setPage(targetPage);
      setGoToPageInput('');
    } else {
      toast.error(`Please enter a valid page between 1 and ${totalPages}`);
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (page > 3) {
        pages.push('...');
      }

      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);

      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) pages.push(i);
      }

      if (page < totalPages - 2) {
        pages.push('...');
      }

      if (!pages.includes(totalPages)) pages.push(totalPages);
    }

    return pages.map((p, idx) => {
      if (p === '...') {
        return <span key={`dots-${idx}`} className="pagination-dots">...</span>;
      }
      return (
        <button
          key={`page-${p}`}
          onClick={() => setPage(p)}
          className={`page-num-btn ${page === p ? 'active' : ''}`}
        >
          {p}
        </button>
      );
    });
  };

  return (
    <div className="search-patient-container">
      {!selectedPatient ? (
        <>
          <div className="directory-header-row">
            <div className="directory-search-wrapper">
              <Search size={18} className="dir-search-icon" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search patient"
              />
              {searchTerm && (
                <button type="button" className="clear-search-btn-v3" onClick={() => setSearchTerm('')}>×</button>
              )}
            </div>
            <div className="directory-sort-wrapper">
              <select value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(1); }}>
                <option value="recent">Sort by: Recent Visit</option>
                <option value="name">Sort by: Name (A-Z)</option>
                <option value="age">Sort by: Age (Desc)</option>
              </select>
            </div>
          </div>

          <div className="results-section">
            {loading && patients.length === 0 ? (
              <div className="loading-state-directory">
                <p>Loading patient directory...</p>
              </div>
            ) : patients.length === 0 ? (
              <div className="empty-state-directory">
                <p>No patients found matching your search.</p>
              </div>
            ) : (
              <>
                <div className="directory-table-container">
                  <table className="directory-table">
                    <thead>
                      <tr>
                        <th>Patient</th>
                        <th>Phone</th>
                        <th>Conditions</th>

                        <th>Last Visit</th>
                        <th>Action</th>
                        <th style={{ width: '50px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {patients.map(patient => {
                        return (
                          <tr 
                            key={patient.id}
                            onClick={() => handleViewDetails(patient)}
                            className="clickable-row"
                          >
                            <td>
                              <div className="table-patient-cell">
                                <div className="table-avatar">
                                  {patient.name?.charAt(0).toUpperCase()}
                                </div>
                                <div className="table-patient-info">
                                  <span className="p-name">{patient.name}</span>
                                  <span className="p-age">{patient.age} years • {patient.gender}</span>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="table-phone">+91 {patient.phone}</span>
                            </td>
                            <td>
                              <span className="table-text">
                                {patient.conditions && patient.conditions.toLowerCase() !== 'none' && patient.conditions.toLowerCase() !== 'n/a'
                                  ? patient.conditions
                                  : 'None'}
                              </span>
                            </td>
                            <td>
                              <span className="table-text">
                                {patient.last_visit
                                  ? new Date(patient.last_visit).toLocaleDateString()
                                  : 'First Visit'}
                              </span>
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <button
                                className="btn-action-rx-table"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStartPrescription(patient);
                                }}
                              >
                                Start Prescription
                              </button>
                            </td>
                            <td onClick={(e) => e.stopPropagation()} style={{ position: 'relative' }}>
                              <button
                                className="btn-table-menu"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveMenuId(activeMenuId === patient.id ? null : patient.id);
                                }}
                              >
                                ⋮
                              </button>
                              {activeMenuId === patient.id && (
                                <>
                                  <div className="menu-backdrop" onClick={() => setActiveMenuId(null)} />
                                  <div className="table-action-menu">
                                    <button onClick={() => { setActiveMenuId(null); handleViewDetails(patient); }}>
                                      View History
                                    </button>
                                    <button onClick={() => { setActiveMenuId(null); handleStartPrescription(patient); }}>
                                      Consult
                                    </button>
                                  </div>
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {totalPages >= 1 && (
                  <div className="directory-pagination-v3">
                    <div className="pagination-left">
                      <span>Page {page} of {totalPages}</span>
                    </div>

                    <div className="pagination-center">
                      <button
                        onClick={handlePrevPage}
                        disabled={page === 1}
                        className="btn-page-arrow-v3"
                      >
                        &lt; Previous
                      </button>

                      {renderPageNumbers()}

                      <button
                        onClick={handleNextPage}
                        disabled={page === totalPages}
                        className="btn-page-arrow-v3"
                      >
                        Next &gt;
                      </button>
                    </div>

                    <form onSubmit={handleGoToPage} className="pagination-right">
                      <span>Go to</span>
                      <input
                        type="number"
                        min="1"
                        max={totalPages}
                        value={goToPageInput}
                        onChange={(e) => setGoToPageInput(e.target.value)}
                        placeholder="Page"
                      />
                      <button type="submit" className="btn-go-page">Page</button>
                    </form>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      ) : (
        <div className="patient-detail-view">
          <div className="detail-header">
            <button onClick={() => { setSelectedPatient(null); setPatientHistory([]); setIsEditing(false); }} className="btn-back">
              ← Back to Search
            </button>
            <div className="detail-actions">
              <button 
                className="btn-secondary" 
                onClick={() => onIssueCertificate && onIssueCertificate(selectedPatient)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                Issue Certificate
              </button>
              {!isEditing && (
                <button onClick={() => setIsEditing(true)} className="btn-secondary">
                  Edit Profile
                </button>
              )}
              <button onClick={() => handleStartPrescription(selectedPatient)} className="btn-primary">
                New Prescription
              </button>
            </div>
          </div>

          {isEditing ? (
            <div className="patient-profile-card">
              <form onSubmit={handleUpdatePatient} className="edit-profile-form" style={{ padding: '1rem' }}>
                <h3 style={{ marginBottom: '1.5rem', color: 'var(--gray-900)' }}>Edit Patient Information</h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input type="text" className="form-input" value={editForm.name} onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))} required />
                  </div>
                  <div className="form-group">
                    <label>Phone Number *</label>
                    <input type="tel" className="form-input" value={editForm.phone} onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))} required />
                  </div>
                  <div className="form-group">
                    <label>Age *</label>
                    <input type="number" className="form-input" value={editForm.age} onChange={(e) => setEditForm(prev => ({ ...prev, age: e.target.value }))} required />
                  </div>
                  <div className="form-group">
                    <label>Gender</label>
                    <select className="form-input" value={editForm.gender} onChange={(e) => setEditForm(prev => ({ ...prev, gender: e.target.value }))}>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                {editForm.conditions && (
                  <div className="form-group" style={{ marginBottom: '1rem' }}>
                    <label>Past Medical History</label>
                    <div style={{ padding: '0.75rem', background: 'var(--gray-50)', borderRadius: 'var(--radius-md)', color: 'var(--gray-700)', border: '1px solid var(--gray-200)' }}>
                      {editForm.conditions}
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', borderTop: '1px solid var(--gray-200)', paddingTop: '1.5rem' }}>
                  <button type="button" onClick={() => { setIsEditing(false); setEditForm({ ...selectedPatient }); }} className="btn-secondary" disabled={loading}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={loading}>
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <div className="patient-profile-card">
              <div className="profile-header">
                <div className="profile-avatar-large">{selectedPatient.name?.charAt(0).toUpperCase()}</div>
                <div className="profile-info">
                  <h2>
                    {selectedPatient.name}
                    {selectedPatient.primary_contact_id ? (
                      <span className="relationship-badge family-badge" style={{ marginLeft: '0.75rem', fontSize: '0.75rem', padding: '0.125rem 0.5rem', background: '#e2e8f0', color: '#475569', borderRadius: '12px', textTransform: 'capitalize', fontWeight: 'bold', display: 'inline-block', verticalAlign: 'middle' }}>
                        {selectedPatient.relationship || 'Family Member'}
                      </span>
                    ) : familyList.length > 0 ? (
                      <span className="relationship-badge primary-badge" style={{ marginLeft: '0.75rem', fontSize: '0.75rem', padding: '0.125rem 0.5rem', background: '#dbeafe', color: '#1e40af', borderRadius: '12px', textTransform: 'capitalize', fontWeight: 'bold', display: 'inline-block', verticalAlign: 'middle' }}>
                        Primary Contact
                      </span>
                    ) : null}
                  </h2>
                  <div className="profile-meta">
                    <span>{selectedPatient.age} years</span>
                    <span>•</span>
                    <span>{selectedPatient.gender}</span>
                    <span>•</span>
                    <span>{selectedPatient.phone}</span>
                  </div>
                </div>
              </div>
              <div className="profile-details">
                <div className="detail-section">
                  <h3>Patient Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="label">Last Visit:</span>
                      <span className="value">
                        {selectedPatient.last_visit ? new Date(selectedPatient.last_visit).toLocaleDateString() : 'First visit'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Registered:</span>
                      <span className="value">{new Date(selectedPatient.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                {selectedPatient.conditions && (
                  <div className="detail-section">
                    <h3>Medical History</h3>
                    {selectedPatient.conditions && (
                      <div className="detail-item">
                        <span className="label">Past Medical History:</span>
                        <span className="value">{selectedPatient.conditions}</span>
                      </div>
                    )}

                  </div>
                )}
                {familyList.length > 0 && (
                  <div className="detail-section" style={{ width: '100%' }}>
                    <h3>Family Members</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
                      {familyList.map(member => (
                        <div 
                          key={member.id} 
                          onClick={() => handleViewDetails(member)}
                          style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            padding: '0.625rem 1rem', 
                            background: 'var(--gray-50)', 
                            borderRadius: '12px', 
                            cursor: 'pointer',
                            border: '1px solid var(--gray-200)',
                            transition: 'all 0.2s'
                          }}
                          className="hover-grow"
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--gray-200) 0%, var(--gray-300) 100%)', color: 'var(--gray-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '0.8rem' }}>
                              {member.name?.charAt(0).toUpperCase()}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--gray-800)' }}>
                                {member.name}
                                <span style={{ fontSize: '0.7rem', color: 'var(--gray-500)', marginLeft: '0.5rem', textTransform: 'capitalize', padding: '0.1rem 0.35rem', background: 'var(--gray-200)', borderRadius: '6px', fontWeight: 'bold' }}>
                                  {member.relationship || (member.primary_contact_id ? 'Family' : 'Primary Contact')}
                                </span>
                              </span>
                            </div>
                          </div>
                          <span style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>
                            {member.gender} • {member.age} Years • +91 {member.phone}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="history-section">
            <h3>Visit History ({patientHistory.length})</h3>
            {loading ? (
              <p>Loading history...</p>
            ) : patientHistory.length === 0 ? (
              <div className="empty-state"><p>No previous visits recorded</p></div>
            ) : (
              <div className="history-list">
                {patientHistory.map(prescription => (
                  <div key={prescription.id} className="history-card">
                    <div className="history-header">
                      <div className="history-date">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                        <span>{new Date(prescription.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className={`status-badge ${prescription.status}`}>{prescription.status}</div>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        {prescription.lab_status === 'report_uploaded' && (
                          <a 
                            href={`${window.location.origin}/app/report/${prescription.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-view-rx"
                            style={{ background: '#ecfdf5', color: '#059669', borderColor: '#a7f3d0', textDecoration: 'none' }}
                            title="View Lab Report"
                          >
                            View Report
                          </a>
                        )}
                        <button 
                          className="btn-view-rx" 
                          onClick={() => setViewingRx(prescription)}
                          title="View Digital Prescription"
                        >
                          View Digital Rx
                        </button>
                      </div>
                    </div>
                    <div className="history-content">
                      <div className="history-item"><strong>Diagnosis:</strong> {prescription.diagnosis}</div>
                      {prescription.symptoms && (
                        <div className="history-item"><strong>Symptoms:</strong> {prescription.symptoms}</div>
                      )}
                      {prescription.medicines?.length > 0 && (
                        <div className="history-item">
                          <strong>Medicines:</strong>
                          <ul>
                            {prescription.medicines.slice(0, 3).map((med, idx) => (
                              <li key={idx}>{med.name} - {med.dosage} {med.frequency}</li>
                            ))}
                            {prescription.medicines.length > 3 && <li>+{prescription.medicines.length - 3} more</li>}
                          </ul>
                        </div>
                      )}
                      {prescription.tests?.length > 0 && (
                        <div className="history-item"><strong>Tests:</strong> {prescription.tests.join(', ')}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {viewingRx && (
        <PrescriptionViewer
          prescription={viewingRx}
          mode="view"
          onClose={() => setViewingRx(null)}
        />
      )}
    </div>
  );
}

export default SearchPatient;